/**
 * Theme presets — HSL values for the design tokens defined in globals.css.
 * Each preset overrides just the *brand* tokens; surfaces/neutrals stay
 * consistent so every preset still feels like the same product.
 *
 * To add a preset: append a ThemePreset record. The label appears in the
 * theme settings sheet automatically.
 */

export interface ThemePalette {
  primary: string;
  primaryForeground: string;
  ring: string;
  accent: string;
  accentForeground: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarMuted: string;
  sidebarAccent: string;
  sidebarBorder: string;
}

export interface ThemePreset {
  id: string;
  label: string;
  /** Swatch color used in the preset picker UI. */
  swatch: string;
  light: ThemePalette;
  dark: ThemePalette;
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'navy',
    label: 'Navy',
    swatch: '#1e293b',
    light: {
      primary: '222 47% 17%',
      primaryForeground: '210 40% 98%',
      ring: '222 47% 25%',
      accent: '217 91% 95%',
      accentForeground: '222 47% 17%',
      sidebar: '222 47% 11%',
      sidebarForeground: '210 40% 98%',
      sidebarMuted: '215 25% 65%',
      sidebarAccent: '222 47% 17%',
      sidebarBorder: '222 40% 18%',
    },
    dark: {
      primary: '210 40% 98%',
      primaryForeground: '222 47% 11%',
      ring: '215 20% 65%',
      accent: '217 33% 17%',
      accentForeground: '210 40% 98%',
      sidebar: '222 47% 6%',
      sidebarForeground: '210 40% 98%',
      sidebarMuted: '215 16% 55%',
      sidebarAccent: '217 33% 14%',
      sidebarBorder: '217 33% 14%',
    },
  },
  {
    id: 'indigo',
    label: 'Indigo',
    swatch: '#4f46e5',
    light: {
      primary: '239 84% 60%',
      primaryForeground: '0 0% 100%',
      ring: '239 84% 60%',
      accent: '239 100% 96%',
      accentForeground: '239 84% 35%',
      sidebar: '240 45% 12%',
      sidebarForeground: '210 40% 98%',
      sidebarMuted: '232 22% 65%',
      sidebarAccent: '240 35% 20%',
      sidebarBorder: '240 35% 18%',
    },
    dark: {
      primary: '239 84% 67%',
      primaryForeground: '0 0% 100%',
      ring: '239 84% 67%',
      accent: '239 33% 20%',
      accentForeground: '210 40% 98%',
      sidebar: '240 45% 6%',
      sidebarForeground: '210 40% 98%',
      sidebarMuted: '232 16% 55%',
      sidebarAccent: '240 33% 14%',
      sidebarBorder: '240 33% 14%',
    },
  },
  {
    id: 'emerald',
    label: 'Emerald',
    swatch: '#059669',
    light: {
      primary: '160 84% 30%',
      primaryForeground: '0 0% 100%',
      ring: '160 84% 30%',
      accent: '152 81% 94%',
      accentForeground: '160 84% 20%',
      sidebar: '170 60% 10%',
      sidebarForeground: '152 50% 96%',
      sidebarMuted: '160 18% 60%',
      sidebarAccent: '170 50% 16%',
      sidebarBorder: '170 50% 14%',
    },
    dark: {
      primary: '160 84% 39%',
      primaryForeground: '0 0% 100%',
      ring: '160 84% 39%',
      accent: '160 33% 20%',
      accentForeground: '210 40% 98%',
      sidebar: '170 60% 5%',
      sidebarForeground: '152 50% 96%',
      sidebarMuted: '160 16% 50%',
      sidebarAccent: '170 33% 12%',
      sidebarBorder: '170 33% 12%',
    },
  },
  {
    id: 'crimson',
    label: 'Crimson',
    swatch: '#dc2626',
    light: {
      primary: '0 72% 51%',
      primaryForeground: '0 0% 100%',
      ring: '0 72% 51%',
      accent: '0 86% 95%',
      accentForeground: '0 72% 30%',
      sidebar: '0 35% 12%',
      sidebarForeground: '0 0% 98%',
      sidebarMuted: '0 12% 65%',
      sidebarAccent: '0 30% 20%',
      sidebarBorder: '0 30% 18%',
    },
    dark: {
      primary: '0 72% 58%',
      primaryForeground: '0 0% 100%',
      ring: '0 72% 58%',
      accent: '0 33% 22%',
      accentForeground: '0 0% 98%',
      sidebar: '0 35% 6%',
      sidebarForeground: '0 0% 98%',
      sidebarMuted: '0 12% 55%',
      sidebarAccent: '0 30% 14%',
      sidebarBorder: '0 30% 14%',
    },
  },
  {
    id: 'amber',
    label: 'Amber',
    swatch: '#d97706',
    light: {
      primary: '32 95% 44%',
      primaryForeground: '0 0% 100%',
      ring: '32 95% 44%',
      accent: '48 96% 89%',
      accentForeground: '32 95% 30%',
      sidebar: '24 40% 12%',
      sidebarForeground: '48 100% 96%',
      sidebarMuted: '32 18% 60%',
      sidebarAccent: '24 35% 20%',
      sidebarBorder: '24 35% 18%',
    },
    dark: {
      primary: '32 95% 53%',
      primaryForeground: '0 0% 100%',
      ring: '32 95% 53%',
      accent: '32 33% 22%',
      accentForeground: '48 100% 96%',
      sidebar: '24 40% 6%',
      sidebarForeground: '48 100% 96%',
      sidebarMuted: '32 16% 50%',
      sidebarAccent: '24 33% 14%',
      sidebarBorder: '24 33% 14%',
    },
  },
  {
    id: 'slate',
    label: 'Slate',
    swatch: '#475569',
    light: {
      primary: '215 28% 30%',
      primaryForeground: '210 40% 98%',
      ring: '215 28% 30%',
      accent: '215 28% 92%',
      accentForeground: '215 28% 25%',
      sidebar: '215 25% 15%',
      sidebarForeground: '210 40% 98%',
      sidebarMuted: '215 16% 65%',
      sidebarAccent: '215 25% 22%',
      sidebarBorder: '215 25% 20%',
    },
    dark: {
      primary: '215 28% 75%',
      primaryForeground: '215 28% 12%',
      ring: '215 28% 75%',
      accent: '215 28% 22%',
      accentForeground: '210 40% 98%',
      sidebar: '215 25% 8%',
      sidebarForeground: '210 40% 98%',
      sidebarMuted: '215 12% 55%',
      sidebarAccent: '215 25% 14%',
      sidebarBorder: '215 25% 14%',
    },
  },
];

export const DEFAULT_PRESET_ID = 'navy';
export const DEFAULT_RADIUS = '0.5rem';
export const DEFAULT_DENSITY: Density = 'comfortable';

export type Density = 'compact' | 'comfortable' | 'spacious';
export type ColorMode = 'light' | 'dark' | 'system';

export const DENSITY_OPTIONS: Array<{ id: Density; label: string }> = [
  { id: 'compact', label: 'Compact' },
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'spacious', label: 'Spacious' },
];

export const RADIUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '0rem', label: 'Sharp' },
  { value: '0.25rem', label: 'Subtle' },
  { value: '0.5rem', label: 'Default' },
  { value: '0.75rem', label: 'Rounded' },
  { value: '1rem', label: 'Pill' },
];
